import { z } from 'zod';
import { TenantId } from './ids.js';
import { CurrencyCode, Timezone } from './primitives.js';
/**
 * Where a tenant is in its life. This enum is load-bearing: `authority` on every
 * availability response is derived from it (spec 4.4), so it is the difference
 * between an agent that promises a time and an agent that hedges.
 */
export const TenantStatus = z.enum([
    'onboarding', // being set up; the partner is not connected yet
    'importing', // dual-running with the old system during cutover
    'live', // we hold the book
    'paused', // temporarily not answering
    'offboarded',
]);
export const Tenant = z.object({
    id: TenantId,
    slug: z.string().min(1),
    name: z.string().min(1),
    timezone: Timezone,
    currency: CurrencyCode,
    status: TenantStatus,
});
/**
 * What the partner may rely on (agreement section 3). Every field is **computed**,
 * never stored as free text: the agreement is explicit that declaring a
 * capability you do not have is the only way to break the integration, and the
 * reliable way to avoid that is to make the declaration a function of reality.
 */
export const Capabilities = z.object({
    availability: z.enum(['live', 'approximate', 'none']),
    reserve: z.enum(['direct', 'link', 'none']),
    confirm: z.enum(['instant', 'webhook', 'email']),
    clients: z.enum(['full', 'basic', 'none']),
    cancel: z.enum(['direct', 'link', 'none']),
});
/**
 * `live` only when this system holds the book. `importing` means another system
 * can still take a booking, so anything we say may already be stale — the agent
 * should hedge, and it will, because we told it the truth.
 */
export function authorityFor(status) {
    return status === 'live' ? 'live' : 'approximate';
}
/**
 * The capability declaration for a tenant.
 *
 * `reserve` degrades with the tenant's state rather than being switched off by
 * hand: a paused or offboarded tenant cannot be booked into, and the agent
 * routes around it instead of failing mid-conversation.
 */
export function capabilitiesFor(status) {
    const bookable = status === 'live' || status === 'importing';
    return {
        availability: status === 'paused' || status === 'offboarded' ? 'none' : authorityFor(status),
        reserve: bookable ? 'direct' : 'none',
        confirm: 'instant',
        clients: status === 'offboarded' ? 'none' : 'full',
        cancel: bookable ? 'direct' : 'none',
    };
}
/** Per-tenant scheduling knobs. Defaults are LoveSpa's, so the port is a no-op. */
export const TenantConfig = z.object({
    /** Start granularity for offered slots, in minutes. */
    slot_grid_min: z.number().int().min(5).max(120).default(15),
    /** How far ahead availability may be asked about. */
    horizon_days: z.number().int().min(1).max(365).default(60),
    /** Ceiling on a hold's requested TTL. */
    max_hold_seconds: z.number().int().min(60).max(3600).default(1800),
    /** How far `alternatives` may wander from the ask, in days. */
    alternatives_window_days: z.number().int().min(1).max(30).default(7),
    /** Monotonic; bumped by any write that changes the catalog (agreement S2). */
    catalog_version: z.number().int().min(0).default(1),
});
//# sourceMappingURL=tenant.js.map